import { PieChart, Wallet } from "lucide-react";
import {
  getTotalAum,
  getActiveWalletCount,
  getAumByWalletType,
  getChainDistributionGrouped,
} from "@/features/dashboard/dashboard-selectors";
import { formatUsdValue, UNKNOWN_VALUE_LABEL, usdCompact } from "@/features/valuation/valuation";
import type { AiTopic, ResponseBlock } from "./types";

/**
 * Scoped to the two topics with a real, synchronous data source in this
 * app: AUM and wallets both come from the live `useWallets` list via the same
 * selectors `dashboard.tsx` itself uses.
 * DesignDev's fuller 8-topic registry (transfers, approvals, reconciliation,
 * exceptions, alerts/system-health, treasury) isn't ported — those either
 * fetch from a real async endpoint here (transfers via `useTransferStore`,
 * approvals via `useApprovals()`) that a synchronous topic-match/build
 * pass can't read from, or have no equivalent data model in this app at
 * all (reconciliation/exceptions/treasury). Faking data for those would be
 * exactly the "fabricated UI" this app's auth pages were just deliberately
 * cleaned of — better to under-scope than invent numbers.
 */
const wantsList = (q: string) => /\b(list|show|which|recent|table)\b/i.test(q);
const wantsTrend = (q: string) => /\b(trend|trending|average|compare|forecast|outlook|insight)\b/i.test(q);
const wantsBreakdown = (q: string) => /\b(breakdown|composition|allocation|by (type|chain|asset))\b/i.test(q);

export const TOPICS: AiTopic[] = [
  {
    id: "aum",
    keywords: ["aum", "portfolio", "assets under management", "total value", "holdings"],
    suggestedQuestion: "What is the total AUM?",
    cardTitle: "Portfolio Insights",
    cardDescription: "Get AUM and asset allocation across wallets",
    icon: PieChart,
    accent: "pending",
    buildResponse: (query, { wallets, valuation }) => {
      const totalAum = valuation ?? getTotalAum(wallets);
      const blocks: ResponseBlock[] = [
        { kind: "kpi", items: [{ label: "Total AUM", value: formatUsdValue(totalAum.valuedTotalUsd) }] },
      ];
      if (wantsBreakdown(query) || wantsList(query)) {
        const byType = getAumByWalletType(wallets);
        const byChain = getChainDistributionGrouped(wallets, 0.05);
        const source = /chain/i.test(query) ? byChain : byType;
        blocks.push({
          kind: "breakdown",
          title: /chain/i.test(query) ? "AUM by chain" : "AUM by wallet type",
          items: source.map((s) => ({
            label: "chain" in s ? s.chain : s.type,
            pct: Math.round(s.pct * 1000) / 10,
            value: usdCompact.format(s.value),
          })),
        });
      }
      const top = getAumByWalletType(wallets)[0];
      if (wantsTrend(query) && top) {
        blocks.push({ kind: "insight", text: `${top.type} wallets hold the largest share of AUM at ${Math.round(top.pct * 100)}%.` });
      }
      return {
        summary: totalAum.valuedTotalUsd === null
          ? "Total AUM is unavailable because none of the wallets have a current valuation."
          : `Total AUM across valued wallets is ${formatUsdValue(totalAum.valuedTotalUsd)}.`,
        blocks,
        actions: [{ label: "View wallets", href: "/wallets" }],
      };
    },
  },
  {
    id: "wallets",
    keywords: ["wallet", "wallets", "active wallet", "custody", "wallet activity"],
    suggestedQuestion: "List my active wallets",
    cardTitle: "Wallets",
    cardDescription: "Review active wallets and custody status",
    icon: Wallet,
    accent: "success",
    buildResponse: (query, { wallets }) => {
      const active = getActiveWalletCount(wallets);
      const blocks: ResponseBlock[] = [
        { kind: "kpi", items: [
          { label: "Active Wallets", value: String(active) },
          { label: "Total Wallets", value: String(wallets.length) },
        ] },
      ];
      if (wantsList(query)) {
        blocks.push({
          kind: "table",
          columns: ["Wallet", "Chain", "Provider", "Status"],
          // ALL-5638: a wallet the gateway did not label renders as the dash here too.
          rows: wallets.slice(0, 8).map((w) => [w.name, w.chain, w.provider ?? UNKNOWN_VALUE_LABEL, w.status]),
        });
      }
      if (wantsTrend(query)) {
        const [topChain] = getChainDistributionGrouped(wallets, 0.05);
        if (topChain) {
          blocks.push({
            kind: "insight",
            text: `Most wallet value is concentrated on ${topChain.chain} (${Math.round(topChain.pct * 100)}%). This app doesn't yet track historical wallet activity, so a time-based trend isn't available.`,
          });
        }
      }
      return {
        summary: `There ${active === 1 ? "is" : "are"} ${active} active wallet${active === 1 ? "" : "s"}, out of ${wallets.length} total.`,
        blocks,
        actions: [{ label: "View wallets", href: "/wallets" }],
      };
    },
  },
];
